import React from 'react';
import { CheckCircle2, LogOut, PackageCheck, Search, ShieldCheck } from 'lucide-react';

const getApiPath = (path) => {
  if (!path || typeof path !== 'string') return path;
  if (!path.startsWith('/api/')) return path;
  const apiBase = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/+$/, '');
  return apiBase ? `${apiBase}${path}` : path;
};

const apiPost = async (path, body) => {
  const res = await fetch(getApiPath(path), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body || {}),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.error || `Request failed: ${res.status}`);
  return data;
};

const LS_SESSION = 'jpexgo_cashier_session_v1';

const formatGroupedNumber = (value, decimals = 2, trimZeros = true) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  let fixed = n.toFixed(decimals);
  if (trimZeros) fixed = fixed.replace(/\.00$/, '').replace(/(\.\d*[1-9])0+$/, '$1');
  const [intPart, decPart] = fixed.split('.');
  const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return decPart ? `${grouped}.${decPart}` : grouped;
};

const formatMoney = (value, currency = 'сом') => {
  if (value === null || value === undefined || value === '') return '—';
  return `${formatGroupedNumber(value, 2, true)} ${currency}`;
};

const paymentLabel = (status) => {
  const value = String(status || '').trim();
  if (value === 'PROCESSED' || value === 'Оплачено') return 'Оплачено';
  if (value === 'CANCELLED') return 'Отменено';
  return 'Ожидает оплаты';
};

const pickupGroupKey = (order) => {
  const phone = String(order?.phone || '').replace(/\D/g, '');
  const code = String(order?.pickupCode || '').trim();
  const country = String(order?.country || '').trim();
  return code && phone ? `${country}|${phone}|${code}` : `order:${order?.id || ''}`;
};

const groupPickupOrders = (items) => {
  const groups = new Map();
  for (const order of items || []) {
    const key = pickupGroupKey(order);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(order);
  }
  return Array.from(groups.values());
};

const formatDateTimeBishkek = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat('ru-RU', {
    timeZone: 'Asia/Bishkek',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).format(date);
};

const getState = (order) => {
  if (String(order?.status || '').trim() === 'Получен') {
    return {
      title: 'Выдан',
      tone: 'emerald',
      panel: 'border-emerald-400/35 bg-emerald-400/12',
      badge: 'border-emerald-300/35 bg-emerald-300/15 text-emerald-100',
      hint: 'Заказ уже отмечен как выданный.',
    };
  }
  if (paymentLabel(order?.paymentStatus) === 'Оплачено') {
    const paidAtText = order?.paidAt ? ` Оплачено: ${formatDateTimeBishkek(order.paidAt)}.` : '';
    return {
      title: 'Можно выдавать',
      tone: 'yellow',
      panel: 'border-[#FACC15]/35 bg-[#FACC15]/12',
      badge: 'border-[#FACC15]/40 bg-[#FACC15]/15 text-[#FACC15]',
      hint: `Оплата подтверждена.${paidAtText} Проверьте код клиента и выдайте заказ.`,
    };
  }
  return {
    title: 'Ждёт оплаты',
    tone: 'red',
    panel: 'border-red-400/35 bg-red-400/12',
    badge: 'border-red-300/35 bg-red-300/15 text-red-100',
    hint: 'Для выдачи нужно дождаться подтверждения QR-оплаты.',
  };
};

const Shell = ({ children }) => (
  <div className="min-h-screen overflow-hidden bg-[linear-gradient(135deg,#172033,#24324a_52%,#111827)] text-white">
    <div className="pointer-events-none fixed -top-32 -right-24 h-96 w-96 rounded-full bg-[#FACC15]/24 blur-3xl" />
    <div className="pointer-events-none fixed -bottom-32 -left-24 h-96 w-96 rounded-full bg-[#60A5FA]/22 blur-3xl" />
    <div className="relative mx-auto max-w-6xl px-4 py-5 md:px-8">{children}</div>
  </div>
);

const BrandHeader = ({ cashier, onLogout }) => (
  <div className="rounded-[28px] border border-white/14 bg-white/6 p-4 shadow-[0_24px_90px_rgba(0,0,0,0.28)] backdrop-blur-xl">
    <div className="flex items-center justify-between gap-4">
      <div className="flex items-center gap-3">
        <div className="flex h-13 w-13 items-center justify-center rounded-2xl border border-[#FACC15]/25 bg-white/8">
          <img src="/logo.png" alt="Japan Express" className="h-10 w-10 object-contain" />
        </div>
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.28em] text-[#FACC15]">Japan Express</div>
          <h1 className="mt-1 text-2xl font-bold tracking-tight">Экран кассира</h1>
          <div className="mt-1 text-xs text-slate-300">{cashier?.fullName || 'Рабочее место пункта выдачи'}</div>
        </div>
      </div>
      {onLogout ? (
        <button
          onClick={onLogout}
          className="flex h-11 items-center gap-2 rounded-2xl border border-white/14 bg-white/8 px-4 text-xs font-semibold text-white hover:bg-white/12"
        >
          <LogOut size={15} /> Выйти
        </button>
      ) : null}
    </div>
  </div>
);

const CashierPage = () => {
  const [sessionToken, setSessionToken] = React.useState(() => localStorage.getItem(LS_SESSION) || '');
  const [cashier, setCashier] = React.useState(null);
  const [sessionChecking, setSessionChecking] = React.useState(() => Boolean(localStorage.getItem(LS_SESSION)));
  const [pin, setPin] = React.useState('');
  const [query, setQuery] = React.useState('');
  const [orders, setOrders] = React.useState([]);
  const [counterOrders, setCounterOrders] = React.useState([]);
  const [selected, setSelected] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [actionLoading, setActionLoading] = React.useState('');
  const [hidingCounterIds, setHidingCounterIds] = React.useState([]);
  const [error, setError] = React.useState('');
  const [message, setMessage] = React.useState('');

  const authBody = React.useCallback((extra = {}) => ({ sessionToken, ...extra }), [sessionToken]);

  const loadCounterOrders = React.useCallback(async () => {
    if (!sessionToken) return;
    try {
      const r = await apiPost('/api/cashier/orders/counter', authBody());
      setCounterOrders(Array.isArray(r?.orders) ? r.orders : []);
    } catch (_) {
      // ignore, the cashier can still search manually
    }
  }, [authBody, sessionToken]);

  React.useEffect(() => {
    if (!sessionToken) {
      setSessionChecking(false);
      return;
    }
    setSessionChecking(true);
    apiPost('/api/cashier/me', { sessionToken })
      .then((r) => setCashier(r?.cashier || null))
      .catch(() => {
        localStorage.removeItem(LS_SESSION);
        setSessionToken('');
        setCashier(null);
      })
      .finally(() => {
        setSessionChecking(false);
      });
  }, [sessionToken]);

  React.useEffect(() => {
    if (!sessionToken || !cashier) return undefined;
    loadCounterOrders();
    const timer = window.setInterval(loadCounterOrders, 7000);
    return () => window.clearInterval(timer);
  }, [cashier, loadCounterOrders, sessionToken]);

  const login = async (event) => {
    event.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);
    try {
      const r = await apiPost('/api/cashier/login', { pin });
      localStorage.setItem(LS_SESSION, r.sessionToken);
      setSessionToken(r.sessionToken);
      setCashier(r.cashier || null);
      setPin('');
    } catch (e) {
      setError(e?.message || 'Не удалось войти');
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      if (sessionToken) await apiPost('/api/cashier/logout', { sessionToken });
    } catch (_) {
      // ignore
    }
    localStorage.removeItem(LS_SESSION);
    setSessionToken('');
    setCashier(null);
    setOrders([]);
    setCounterOrders([]);
    setSelected(null);
  };

  const searchOrders = async (event) => {
    event.preventDefault();
    setError('');
    setMessage('');
    setSelected(null);
    if (query.trim().length < 3) {
      setError('Введите минимум 3 символа: код, трек или телефон');
      return;
    }
    setLoading(true);
    try {
      const r = await apiPost('/api/cashier/orders/search', authBody({ query: query.trim() }));
      const items = Array.isArray(r?.orders) ? r.orders : [];
      setOrders(items);
      const exactCodeGroup = /^\d{4}$/.test(query.trim())
        && items.length > 0
        && items.every((item) => String(item.pickupCode || '') === query.trim());
      if (items.length === 1 || exactCodeGroup) setSelected(items[0]);
      if (!items.length) setMessage('Заказы не найдены');
    } catch (e) {
      setError(e?.message || 'Ошибка поиска');
    } finally {
      setLoading(false);
    }
  };

  const replaceOrder = (nextOrder) => {
    if (!nextOrder) return;
    setSelected(nextOrder);
    setOrders((prev) => prev.map((item) => (String(item.id) === String(nextOrder.id) ? nextOrder : item)));
    setCounterOrders((prev) => {
      if (nextOrder.pickedUpAt) return prev.filter((item) => String(item.id) !== String(nextOrder.id));
      return prev.map((item) => (String(item.id) === String(nextOrder.id) ? nextOrder : item));
    });
  };

  const confirmPickup = async () => {
    if (!selected?.id) return;
    if (!window.confirm('Подтвердить выдачу заказа клиенту?')) return;
    setActionLoading('pickup');
    setError('');
    setMessage('');
    try {
      const r = await apiPost('/api/cashier/pickup/confirm-group', authBody({ orderId: selected.id }));
      const updatedOrders = Array.isArray(r?.orders) ? r.orders : [];
      const updatedIds = new Set(updatedOrders.map((order) => String(order.id)));
      setOrders((prev) => prev.map((order) => updatedOrders.find((item) => String(item.id) === String(order.id)) || order));
      setCounterOrders((prev) => prev.filter((order) => !updatedIds.has(String(order.id))));
      setSelected(updatedOrders[0] || r?.order || null);
      setMessage(updatedOrders.length > 1 ? `Выдано заказов: ${updatedOrders.length}` : 'Заказ отмечен как выданный');
    } catch (e) {
      setError(e?.message || 'Не удалось выдать заказ');
    } finally {
      setActionLoading('');
    }
  };

  const removeFromCounter = async (orderId) => {
    if (!orderId) return;
    setHidingCounterIds((prev) => Array.from(new Set([...prev, String(orderId)])));
    setActionLoading(`counter-remove-${orderId}`);
    setError('');
    setMessage('');
    try {
      const r = await apiPost('/api/cashier/orders/counter/remove', authBody({ orderId }));
      const removedIds = new Set((r?.orderIds || [orderId]).map(String));
      window.setTimeout(() => {
        setCounterOrders((prev) => prev.filter((item) => !removedIds.has(String(item.id))));
        setHidingCounterIds((prev) => prev.filter((id) => !removedIds.has(String(id))));
      }, 260);
      if (removedIds.has(String(selected?.id))) {
        setSelected((prev) => (prev ? { ...prev, clientAtCounter: false, pickupRequestedAt: '' } : prev));
      }
      setMessage('Клиент убран из списка у стойки');
    } catch (e) {
      setHidingCounterIds((prev) => prev.filter((id) => id !== String(orderId)));
      setError(e?.message || 'Не удалось убрать клиента из списка');
    } finally {
      setActionLoading('');
    }
  };

  if (sessionChecking && sessionToken && !cashier) {
    return (
      <Shell>
        <div className="mx-auto flex min-h-[78vh] max-w-md items-center">
          <div className="w-full rounded-[30px] border border-white/14 bg-white/6 p-5 text-center shadow-[0_24px_90px_rgba(0,0,0,0.34)] backdrop-blur-xl">
            <BrandHeader />
            <div className="mt-5 rounded-3xl border border-white/14 bg-white/8 px-4 py-6 text-sm font-semibold text-slate-200">
              Проверяем сессию кассира...
            </div>
          </div>
        </div>
      </Shell>
    );
  }

  if (!sessionToken || !cashier) {
    return (
      <Shell>
        <div className="mx-auto flex min-h-[78vh] max-w-md items-center">
          <div className="w-full rounded-[30px] border border-white/14 bg-white/6 p-5 shadow-[0_24px_90px_rgba(0,0,0,0.34)] backdrop-blur-xl">
            <BrandHeader />
            <div className="mt-5 rounded-3xl border border-[#FACC15]/25 bg-[#FACC15]/10 p-4">
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#FACC15]/15 text-[#FACC15]">
                  <ShieldCheck size={22} />
                </div>
                <div>
                  <h2 className="text-lg font-semibold">Вход кассира</h2>
                  <p className="text-xs text-slate-300">Введите личный PIN из 3 цифр.</p>
                </div>
              </div>
              <form onSubmit={login} className="mt-5 space-y-3">
                <input
                  value={pin}
                  onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 3))}
                  inputMode="numeric"
                  placeholder="PIN"
                  className="h-14 w-full rounded-2xl border border-white/20 bg-white px-4 text-center text-3xl font-black tracking-[0.55em] text-slate-950 outline-none placeholder:text-slate-400 focus:border-[#FACC15]/70"
                />
                <button
                  type="submit"
                  disabled={loading || pin.length !== 3}
                  className="h-12 w-full rounded-2xl bg-[#FACC15] text-sm font-black text-slate-950 shadow-[0_14px_45px_rgba(250,204,21,0.18)] disabled:opacity-60"
                >
                  {loading ? 'Проверяем...' : 'Войти'}
                </button>
              </form>
              {error ? <div className="mt-3 rounded-2xl border border-red-400/25 bg-red-400/10 px-3 py-2 text-xs text-red-200">{error}</div> : null}
            </div>
          </div>
        </div>
      </Shell>
    );
  }

  const counterGroups = groupPickupOrders(counterOrders);
  const selectedGroup = selected
    ? groupPickupOrders(orders).find((group) => pickupGroupKey(group[0]) === pickupGroupKey(selected)) || [selected]
    : [];
  const selectedPaid = selectedGroup.length > 0 && selectedGroup.every((order) => paymentLabel(order.paymentStatus) === 'Оплачено');
  const selectedPicked = selectedGroup.length > 0 && selectedGroup.every((order) => String(order.status || '').trim() === 'Получен');
  const selectedTotalSom = selectedGroup.reduce((sum, order) => sum + Number(order.amountSom || 0), 0);
  const selectedStateOrder = selected ? { ...selected, paymentStatus: selectedPaid ? 'Оплачено' : 'Ожидает оплаты' } : null;
  const state = getState(selectedStateOrder);

  return (
    <Shell>
      <BrandHeader cashier={cashier} onLogout={logout} />

      <div className="mt-5 grid gap-5 lg:grid-cols-[420px_1fr]">
        <div className="space-y-4">
          <div className="rounded-[28px] border border-[#FACC15]/30 bg-[#FACC15]/10 p-4 backdrop-blur-xl">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#FACC15]">Клиенты у стойки</div>
                <div className="mt-1 text-xs text-slate-300">Клиент ввёл код на планшете и ожидает выдачу.</div>
              </div>
              <div className="rounded-full bg-[#FACC15] px-3 py-1 text-xs font-black text-slate-950">{counterGroups.length}</div>
            </div>

            {counterOrders.length ? (
              <div className="mt-3 space-y-2">
                {counterGroups.map((group) => {
                  const order = group[0];
                  return (
                  <div
                    key={order.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => { setOrders(group); setSelected(order); }}
                    onKeyDown={(event) => {
                      if (event.key !== 'Enter' && event.key !== ' ') return;
                      event.preventDefault();
                      setOrders(group);
                      setSelected(order);
                    }}
                    className={`w-full rounded-2xl border p-3 text-left transition-all duration-300 ${
                      hidingCounterIds.includes(String(order.id))
                        ? 'scale-[0.98] opacity-0 -translate-y-2'
                        : 'scale-100 opacity-100 translate-y-0'
                    } ${
                      String(selected?.id) === String(order.id)
                        ? 'border-[#FACC15]/70 bg-[#FACC15]/18'
                        : 'border-white/14 bg-white/8 hover:bg-white/12'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="font-mono text-sm font-black text-white">{order.pickupCode || order.trackNumber}</div>
                        <div className="mt-1 break-all font-mono text-xs text-slate-300">{order.trackNumber}</div>
                        {group.length > 1 ? <div className="mt-1 text-xs font-bold text-[#FACC15]">Треков: {group.length}</div> : null}
                        <div className="mt-1 text-xs text-slate-300">{order.fullName || 'Без ФИО'}</div>
                      </div>
                      <div className="rounded-full border border-emerald-300/30 bg-emerald-400/15 px-2 py-1 text-[10px] font-bold text-emerald-100">
                        У стойки
                      </div>
                    </div>
                    <div className="mt-2 text-xs text-white">{paymentLabel(order.paymentStatus)} · {order.status}</div>
                    <div className="mt-3 flex items-center justify-between gap-2">
                      <div className="text-[11px] text-slate-300">Нажмите карточку, чтобы открыть заказ</div>
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={(event) => {
                          event.stopPropagation();
                          removeFromCounter(order.id);
                        }}
                        onKeyDown={(event) => {
                          if (event.key !== 'Enter' && event.key !== ' ') return;
                          event.preventDefault();
                          event.stopPropagation();
                          removeFromCounter(order.id);
                        }}
                        className="rounded-xl border border-white/14 bg-white/8 px-3 py-2 text-xs font-bold text-white hover:bg-white/12"
                      >
                        {actionLoading === `counter-remove-${order.id}` ? 'Скрываем...' : 'Скрыть'}
                      </span>
                    </div>
                  </div>
                  );
                })}
              </div>
            ) : (
              <div className="mt-3 rounded-2xl border border-white/10 bg-white/6 px-3 py-3 text-xs text-slate-300">
                Пока никто не отметил заказ на планшете.
              </div>
            )}
          </div>

          <div className="rounded-[28px] border border-white/14 bg-white/6 p-4 backdrop-blur-xl">
            <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-400">Поиск заказа</div>
            <form onSubmit={searchOrders} className="mt-3 space-y-3">
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Код, трек или телефон"
                className="h-14 w-full rounded-2xl border border-white/20 bg-white px-4 text-lg font-semibold text-slate-950 outline-none placeholder:text-slate-400 focus:border-[#FACC15]/70"
              />
              <button
                disabled={loading}
                className="flex h-12 w-full items-center justify-center gap-2 rounded-2xl bg-[#FACC15] text-sm font-black text-slate-950 disabled:opacity-60"
              >
                <Search size={17} /> {loading ? 'Ищем...' : 'Найти заказ'}
              </button>
            </form>
            <div className="mt-3 text-[11px] leading-relaxed text-slate-300">
              Клиент может показать 4-значный код, трек или назвать телефон. Телефон на экране клиента не показываем.
            </div>
            {error ? <div className="mt-3 rounded-2xl border border-red-400/25 bg-red-400/10 px-3 py-2 text-xs text-red-200">{error}</div> : null}
            {message ? <div className="mt-3 rounded-2xl border border-emerald-400/25 bg-emerald-400/10 px-3 py-2 text-xs text-emerald-200">{message}</div> : null}
          </div>

          {orders.length > 1 ? (
            <div className="rounded-[28px] border border-white/14 bg-white/6 p-4 backdrop-blur-xl">
              <div className="mb-3 text-sm font-semibold">Найдено: {orders.length}</div>
              <div className="space-y-2">
                {orders.map((order) => (
                  <button
                    key={order.id}
                    onClick={() => setSelected(order)}
                    className={`w-full rounded-2xl border p-3 text-left transition ${
                      String(selected?.id) === String(order.id)
                        ? 'border-[#FACC15]/55 bg-[#FACC15]/12'
                        : 'border-white/14 bg-white/8 hover:bg-white/12'
                    }`}
                  >
                    <div className="font-mono text-xs text-white">{order.trackNumber}</div>
                    <div className="mt-1 text-xs text-slate-300">{order.fullName || 'Без ФИО'} · {order.phone || 'без телефона'}</div>
                    <div className="mt-2 text-xs text-white">{paymentLabel(order.paymentStatus)} · {order.status}</div>
                  </button>
                ))}
              </div>
            </div>
          ) : null}
        </div>

        <div>
          {selected ? (
            <div className={`rounded-[32px] border ${state.panel} p-5 shadow-[0_28px_120px_rgba(0,0,0,0.38)] backdrop-blur-xl`}>
              <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
                <div>
                  <div className={`inline-flex rounded-full border px-3 py-1 text-xs font-bold ${state.badge}`}>{state.title}</div>
                  <div className="mt-4 text-[11px] uppercase tracking-[0.22em] text-slate-400">
                    {selectedGroup.length > 1 ? `Треки · ${selectedGroup.length}` : 'Трек'}
                  </div>
                  {selectedGroup.length > 1 ? (
                    <div className="mt-2 max-h-40 space-y-2 overflow-y-auto rounded-2xl border border-white/14 bg-white/8 p-3">
                      {selectedGroup.map((order) => (
                        <div key={order.id} className="flex items-center justify-between gap-3 border-b border-white/10 pb-2 last:border-0 last:pb-0">
                          <span className="break-all font-mono text-sm font-black text-white">{order.trackNumber}</span>
                          <span className="shrink-0 text-xs font-bold text-slate-300">{formatMoney(order.amountSom)}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="mt-1 break-all font-mono text-2xl font-black text-white">{selected.trackNumber}</div>
                  )}
                  <div className="mt-3 text-lg font-semibold">{selected.fullName || 'ФИО не указано'}</div>
                  <div className="mt-1 text-sm text-slate-300">{selected.phone || 'Телефон не указан'}</div>
                </div>
                <div className="min-w-[220px] rounded-[28px] border border-white/14 bg-white/8 p-5 text-center">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-400">Код выдачи</div>
                  <div className="mt-2 font-mono text-5xl font-black tracking-[0.18em] text-[#FACC15]">{selected.pickupCode || '—'}</div>
                </div>
              </div>

              <div className="mt-5 grid gap-3 md:grid-cols-4">
                {[
                  ['Статус', selected.status || '—'],
                  ['Оплата', selectedPaid ? 'Оплачено' : 'Ожидает оплаты'],
                  ['Способ', selectedGroup.map((order) => order.paymentMethod).find(Boolean) || '—'],
                  ['Сумма', formatMoney(selectedTotalSom)],
                ].map(([label, value]) => (
                  <div key={label} className="rounded-2xl border border-white/14 bg-white/8 px-4 py-3">
                    <div className="text-[11px] uppercase tracking-wide text-slate-400">{label}</div>
                    <div className="mt-1 text-sm font-bold text-white">{value}</div>
                  </div>
                ))}
              </div>

              <div className="mt-5 rounded-2xl border border-white/14 bg-white/8 px-4 py-3 text-sm text-slate-300">{state.hint}</div>

              <div className="mt-5 grid gap-3">
                <button
                  type="button"
                  onClick={confirmPickup}
                  disabled={actionLoading || !selectedPaid || selectedPicked}
                  className="flex h-14 items-center justify-center gap-2 rounded-2xl bg-emerald-400 text-sm font-black text-black transition hover:brightness-110 disabled:opacity-40"
                >
                  <PackageCheck size={19} /> {actionLoading === 'pickup' ? 'Выдаём...' : selectedGroup.length > 1 ? `Выдать ${selectedGroup.length} заказа` : 'Выдать заказ'}
                </button>
              </div>

              {selectedPicked ? (
                <div className="mt-4 flex items-center gap-2 rounded-2xl bg-emerald-500/15 px-3 py-2 text-sm text-emerald-100">
                  <CheckCircle2 size={17} /> Заказ уже выдан{selected.pickedUpAt ? `: ${new Date(selected.pickedUpAt).toLocaleString('ru-RU')}` : ''}
                </div>
              ) : null}
            </div>
          ) : (
            <div className="flex min-h-[430px] items-center justify-center rounded-[32px] border border-white/14 bg-white/6 p-8 text-center backdrop-blur-xl">
              <div>
                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-3xl border border-[#FACC15]/25 bg-[#FACC15]/10 text-[#FACC15]">
                  <Search size={27} />
                </div>
                <div className="mt-4 text-xl font-bold">Найдите заказ</div>
                <div className="mt-2 max-w-sm text-sm text-slate-300">
                  После поиска здесь появится большая карточка заказа, код выдачи и действия кассира.
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </Shell>
  );
};

export default CashierPage;
